import { memo } from 'react'
import { useI18n } from '@muse/i18n'
import { useIsHydrating } from '@muse-web/router'
import { useIsMobileOrTablet } from '@core/media'
import { useActions, useValues } from '@core/store'
import { useLogout } from '@features/auth'
import { useCurrentUser } from '@features/session'
import { sendTrySubscriptionClick } from '@features/try-for-free'
import { HeaderActions } from '../components/header-actions'
import type { THeaderLabels, TMenuItemView } from '../components/types'
import { userMenuClick } from '../helpers/navAnalytics'
import { useHeaderActions } from '../helpers/useHeaderActions'
import { getUserMenuItems } from '../helpers/userMenuItems'
import { headerStore } from '../store/headerStore'
import { SearchContainer } from './SearchContainer'

export const HeaderActionsContainer = memo(() => {
    const { t } = useI18n()
    const { username, displayName, avatarUrl } = useCurrentUser()
    const [isMobileMenuOpen] = useValues([headerStore.props.getIsMobileMenuOpen])
    const { toggleMobileMenu, openMobileUserMenu } = useActions({
        toggleMobileMenu: headerStore.actions.toggleMobileMenu,
        openMobileUserMenu: headerStore.actions.openMobileUserMenu,
    })
    const isMobileOrTablet = useIsMobileOrTablet()
    const { isAuthenticated, showTryFree, tryFreeHref, onSignIn, onUpload, closeMobileMenu } =
        useHeaderActions()
    // The SSR header is guest-only, so an authed first render breaks hydration and Preact keeps it.
    const isHydrating = useIsHydrating()
    const { logout } = useLogout()

    const resolvedDisplayName = displayName || username

    const userMenuItems: TMenuItemView[] = getUserMenuItems(username).map(item => ({
        label: t(item.labelKey),
        href: item.href,
        Icon: item.Icon,
        onClick: userMenuClick(item.analytics),
    }))

    const labels: THeaderLabels = {
        upload: t('i18n.headerUpload'),
        tryFree: t('i18n.headerTryFree'),
        logOut: t('i18n.headerLogOut'),
        openMenu: t('i18n.headerOpenMenu'),
        new: t('i18n.headerNew'),
    }

    const handleToggleMobileMenu = () => {
        toggleMobileMenu()
    }

    const handleTryFree = () => {
        sendTrySubscriptionClick({ name: 'header_try_click', payload: {} })
    }

    const handleLogout = () => {
        logout()
        closeMobileMenu()
    }

    return (
        <HeaderActions
            isAuthenticated={isAuthenticated && !isHydrating}
            user={{ displayName: resolvedDisplayName, avatarUrl }}
            userMenuItems={userMenuItems}
            labels={labels}
            search={<SearchContainer />}
            showTryFree={showTryFree}
            isMobileMenuOpen={isMobileMenuOpen}
            isUserMenuDropdown={!isMobileOrTablet}
            tryFreeHref={tryFreeHref}
            onSignIn={onSignIn}
            onToggleMobileMenu={handleToggleMobileMenu}
            onOpenAccountPanel={openMobileUserMenu}
            onLogout={handleLogout}
            onUpload={onUpload}
            onTryFree={handleTryFree}
        />
    )
})
